import { request } from '@/api/client'

/** PUT /developer/apps/:appId/events/request-url，保存事件请求地址。 */
export function saveEventRequestUrl(appId: string, requestUrl: string) {
  return request<unknown>(`/developer/apps/${appId}/events/request-url`, {
    method: 'PUT',
    body: { request_url: requestUrl },
  })
}

export interface EventCatalogQuery {
  /** 分类码，精确匹配。不传则不按分类收窄 */
  category?: string
  /** 匹配事件 Code、名称或说明 */
  keyword?: string
}

export interface CatalogEventItem {
  eventCode: string
  name: string
  description: string
  scopes: string[]
  categoryCode: string
}

export interface CatalogCategory {
  code: string
  name: string
  events: CatalogEventItem[]
}

export interface EventCatalog {
  categories: CatalogCategory[]
}

interface EventCatalogEventDto {
  event_code?: string
  name?: string
  description?: string
  scopes?: unknown[]
}

interface EventCatalogCategoryDto {
  code?: string
  name?: string
  events?: EventCatalogEventDto[]
}

interface EventCatalogDto {
  categories?: EventCatalogCategoryDto[]
}

function mapStringList(items: unknown[] | undefined): string[] {
  if (!items) return []
  return items.flatMap((item) => {
    if (typeof item !== 'string') return []
    const value = item.trim()
    return value ? [value] : []
  })
}

export function mapEventCatalog(data: EventCatalogDto | null | undefined): EventCatalog {
  const categories = (data?.categories ?? []).flatMap((category) => {
    const code = category.code?.trim() ?? ''
    if (!code) return []
    const seen = new Set<string>()
    const events = (category.events ?? []).flatMap((event) => {
      const eventCode = event.event_code?.trim() ?? ''
      if (!eventCode || seen.has(eventCode)) return []
      seen.add(eventCode)
      return [
        {
          eventCode,
          name: event.name?.trim() || eventCode,
          description: event.description?.trim() ?? '',
          scopes: mapStringList(event.scopes),
          categoryCode: code,
        },
      ]
    })
    return [
      {
        code,
        name: category.name?.trim() || code,
        events,
      },
    ]
  })
  return { categories }
}

/** GET /developer/apps/:appId/events/catalog，可订阅事件目录。 */
export function getEventCatalog(appId: string, query: EventCatalogQuery = {}) {
  return request<EventCatalogDto | null>(`/developer/apps/${appId}/events/catalog`, {
    method: 'GET',
    params: {
      category: query.category?.trim() || undefined,
      keyword: query.keyword?.trim() || undefined,
    },
  }).then(mapEventCatalog)
}

export interface EventSetting {
  requestUrl: string
  urlVerified: boolean
  callbackUrl: string
  callbackItems: string[]
  verificationTokenMasked: string
}

interface EventSettingDto {
  request_url?: string
  url_verified?: boolean
  callback_url?: string
  callback_items?: unknown[]
  verification_token_masked?: string
}

export function mapEventSetting(data: EventSettingDto | null | undefined): EventSetting {
  return {
    requestUrl: data?.request_url?.trim() ?? '',
    urlVerified: data?.url_verified === true,
    callbackUrl: data?.callback_url?.trim() ?? '',
    callbackItems: mapStringList(data?.callback_items),
    verificationTokenMasked: data?.verification_token_masked?.trim() ?? '',
  }
}

export interface VersionEvent {
  eventCode: string
  name: string
  description: string
  category: string
}

export interface VersionEventList {
  /** 请求地址已通过 Challenge 时为 true；未通过时为 false，且没有事件 */
  visible: boolean
  events: VersionEvent[]
}

interface VersionEventDto {
  event_code?: string
  name?: string
  description?: string
  category?: string
}

interface VersionEventListDto {
  visible?: boolean
  events?: VersionEventDto[]
}

export function mapVersionEvents(
  data: VersionEventListDto | null | undefined,
): VersionEventList {
  const visible = data?.visible === true
  if (!visible) return { visible: false, events: [] }
  const seen = new Set<string>()
  const events = (data?.events ?? []).flatMap((event) => {
    const eventCode = event.event_code?.trim() ?? ''
    if (!eventCode || seen.has(eventCode)) return []
    seen.add(eventCode)
    return [
      {
        eventCode,
        name: event.name?.trim() || eventCode,
        description: event.description?.trim() ?? '',
        category: event.category?.trim() ?? '',
      },
    ]
  })
  return { visible: true, events }
}

/** GET /developer/apps/:appId/versions/:versionId/events，当前草稿已添加的事件。 */
export function getVersionEvents(appId: string, versionId: string) {
  return request<VersionEventListDto | null>(
    `/developer/apps/${appId}/versions/${versionId}/events`,
    { method: 'GET' },
  ).then(mapVersionEvents)
}

interface VerificationTokenDto {
  verification_token?: string
}

function mapVerificationToken(data: VerificationTokenDto | null | undefined): string {
  return data?.verification_token?.trim() ?? ''
}

/** GET /developer/apps/:appId/events/verification-token，查看 Verification Token 明文。 */
export function getVerificationToken(appId: string) {
  return request<VerificationTokenDto | null>(
    `/developer/apps/${appId}/events/verification-token`,
    { method: 'GET' },
  ).then(mapVerificationToken)
}

/** POST /developer/apps/:appId/events/verification-token/reset，重置 Verification Token。 */
export function resetVerificationToken(appId: string) {
  return request<VerificationTokenDto | null>(
    `/developer/apps/${appId}/events/verification-token/reset`,
    { method: 'POST' },
  ).then(mapVerificationToken)
}

/** PUT /developer/apps/:appId/events/verification-token，编辑 Verification Token。 */
export function updateVerificationToken(appId: string, verificationToken: string) {
  return request<VerificationTokenDto | null>(
    `/developer/apps/${appId}/events/verification-token`,
    {
      method: 'PUT',
      body: { verification_token: verificationToken },
    },
  ).then(mapVerificationToken)
}

/** GET /developer/apps/:appId/events/setting，读取事件配置。 */
export function getEventSetting(appId: string) {
  return request<EventSettingDto | null>(`/developer/apps/${appId}/events/setting`, {
    method: 'GET',
  }).then(mapEventSetting)
}

/** POST /developer/apps/:appId/versions/:versionId/events/batch-delete，批量删除开放事件。 */
export function batchDeleteVersionEvents(
  appId: string,
  versionId: string,
  eventCodes: string[],
) {
  return request<unknown>(
    `/developer/apps/${appId}/versions/${versionId}/events/batch-delete`,
    {
      method: 'POST',
      body: { event_codes: eventCodes },
    },
  )
}

/** DELETE /developer/apps/:appId/versions/:versionId/events/:eventCode，删除开放事件。 */
export function deleteVersionEvent(
  appId: string,
  versionId: string,
  eventCode: string,
) {
  return request<unknown>(
    `/developer/apps/${appId}/versions/${versionId}/events/${encodeURIComponent(eventCode)}`,
    { method: 'DELETE' },
  )
}

/** POST /developer/apps/:appId/versions/:versionId/events，添加开放事件。 */
export function addVersionEvents(
  appId: string,
  versionId: string,
  eventCodes: string[],
) {
  return request<unknown>(`/developer/apps/${appId}/versions/${versionId}/events`, {
    method: 'POST',
    body: { event_codes: eventCodes },
  })
}
